/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2020, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./api/QueryRunnerAPI',
	'./optimizations/FactsQueryCombiner',
	'./optimizations/SameCategoryDifferentMeasuresQueryCombiner'
], (APIFactory, QueryRunnerAPI, FactsQueryCombiner, SameCategoryDifferentMeasuresQueryCombiner) => {

	/**
	  * @class QueryGroupOptimizer
	  *
	  * @classdesc groups requests for multiple widgets into 1 request, splits back results back to individual widgets
	  * Has 2 optimizers :
	  * FactsQueryCombiner : combines requests from multiple summary widgets
	  * SameCategoryDifferentMeasuresQueryCombiner : combines requests from multiple widgets with same categories, different measures
	  */
	class QueryGroupOptimizer {
		constructor({ features }) {
			this.scheduledByOptimization = {};
			this.dashboardApi = features['API'];
		}

		getAPI() {
			if (!this.api) {
				this.api = APIFactory.createAPI(this, [QueryRunnerAPI]);
			}
			return this.api;
		}

		/**
		 * @function QueryGroupOptimizer#setup
		 * @description initializes
		 *
		 * @param {Object} queryService
		 * @param {Object} [options] additional options to pass to query runner
		 * @param {Object} [options.groupSummaries] turn on/off group summaries request combining optimization
		 * @param {Object} [options.groupSameCategoryDifferentMeasures] turn on/off same categories different measures request combining optimization
		 */
		setup( queryService, options ) {
			this.queryService = queryService;
			this.dataSources = this.dashboardApi.getFeature('DataSources');	//Unfortunately needed because of circular dependency issue.
			this.createOptimizationHandlers(options);
		}

		createOptimizationHandlers(options={}) {
			this.optimizations = {};
			this.isEnabled = false;
			this.delay = options.collectTime || 1;
			if(options.groupSummaries) {
				const factsQueryOptimizer = new FactsQueryCombiner(this.queryService, this.dataSources);
				this.optimizations[factsQueryOptimizer.getId()] = factsQueryOptimizer;
				this.isEnabled = true;
			}
			if( options.groupSameCategoryDifferentMeasures) {
				const sameCategoryDifferentMeasuresQueryCombiner = new SameCategoryDifferentMeasuresQueryCombiner(this.queryService, this.dataSources);
				this.optimizations[sameCategoryDifferentMeasuresQueryCombiner.getId()] = sameCategoryDifferentMeasuresQueryCombiner;
				this.isEnabled = true;
			}
		}

		runQuery(params) {
			const dataSource = this.dataSources && this.dataSources.getDataSource(params.sourceIdOrModule);
			if(this.isEnabled && dataSource) {
				return dataSource.loadMetadata().then(() => {
					return this.schedule(params);
				});
			} else {
				return this.queryService.runQuery({
					sourceIdOrModule: params.sourceIdOrModule,
					querySpec: params.querySpec,
					requestOptions: params.requestOptions,
					sender: params.senderId
				});
			}
		}

		createRequest(requestParams) {
			let resolveCallback;
			let rejectCallback;
			const promise = new Promise((resolve, reject) => {
				resolveCallback = resolve;
				rejectCallback = reject;
			});
			return { requestParams, promise, resolveCallback, rejectCallback };
		}

		schedule(requestParams) {
			const request = this.createRequest( requestParams );
			let response;
			const optimizer = this.getOptimizer(request);
			if (optimizer) {
				this.scheduleOptimization(optimizer, request);

				if (!this._scheduledRun) {
					this._scheduledRun = setTimeout(() => {
						const scheduledByOptimization = this.scheduledByOptimization;

						// clean the schedule for the next round
						this.scheduledByOptimization = [];
						this._scheduledRun = null;

						// go over the scheduled queries .. and optimize them.

						for (let id in scheduledByOptimization){
							const requests =  scheduledByOptimization[id];
							const responses = this.executeOptimization(id ,requests);
							responses.forEach((response, index)=> {
								response.then(function() {
									requests[index].resolveCallback( ...arguments );
								}).catch(function() {
									requests[index].rejectCallback( ...arguments );
								});
							});
						}


					}, this.delay);
				}
				response = request.promise;
			}else {
				response = this._executeRequest(request);
			}

			return response;
		}

		scheduleOptimization(optimizer, request) {
			if(!this.scheduledByOptimization[optimizer.getId()]){
				this.scheduledByOptimization[optimizer.getId()] = [];
			}
			this.scheduledByOptimization[optimizer.getId()].push(request);
		}

		executeOptimization(id, requests){
			const { groups, responseList } = this.optimizations[id].buildExecutionGroups(requests);

			for (let name in groups) {
				if (groups[name].length > 1) {
					//If there are several requests to be merged into one,
					//run the optimizer to combine the requests and split the responses.
					this.optimizations[id].executeGroup(groups[name]);
				} else {
					//If there's only one request assigned to a grouping key, 
					//we can just execute the request unchanged.
					const singleRequestForGroup = groups[name][0];
					this._executeRequest(singleRequestForGroup.request).then((response) => {
						singleRequestForGroup.response.resolve( response );
					}).catch((jqXHR) => {
						singleRequestForGroup.response.reject( jqXHR );
					});
				}
			}

			return responseList;
		}

		getOptimizer(request) {
			let optimizer = null;
			for (let id in this.optimizations) {
				if (this.optimizations[id].isApplicable(request.requestParams)) {
					optimizer = this.optimizations[id];
					break;
				}
			}
			return optimizer;
		}


		//query layer specific
		//execute v1 query
		_executeRequest(request){
			let { affinityContext, sourceIdOrModule,querySpec,requestOptions,senderId } = request.requestParams;
			return this.queryService.runQuery({
				affinityContext: affinityContext,
				sourceIdOrModule: sourceIdOrModule,
				querySpec: querySpec,
				requestOptions: requestOptions,
				sender: senderId
			});
		}
	}

	return QueryGroupOptimizer;
});
